const EXCESS_REDUCTION_SCHEME_IDS = [1729, 1763, 1769, 1771];

export const getQuotePaymentOption = (quote, paymentOptionName) => {
  const paymentOptions = quote?.paymentOptions;
  const normalizePaymentOptionName = (name) =>
    name?.replace(/instalments/gi, "installments");

  return Array.isArray(paymentOptions)
    ? paymentOptions.find(
        (option) =>
          normalizePaymentOptionName(option.paymentOptionName) ===
          normalizePaymentOptionName(paymentOptionName),
      )
    : undefined;
};

export const getQuoteFinanceCharge = (quote) =>
  quote?.paymentOptions?.[0]?.serviceCharge ?? 0;

export const getQuoteExcessReduction = (quote) => {
  if (!EXCESS_REDUCTION_SCHEME_IDS.includes(quote?.schemeTableId)) {
    return null;
  }

  return quote.schemeTableId === 1769
    ? "£250 within 24 hours"
    : "100% within 12 hours | 50% within 24 hours";
};

export const getQuoteExcesses = (quote) => {
  const excesses = quote?.panelMemberQuoteExcesses ?? [];

  if (quote?.schemeTableId !== 1770) {
    return excesses;
  }

  return excesses.filter((excess) =>
    ["Approved repair", "Approved replacement", "Compulsory"].includes(
      excess.excessShortName,
    ),
  );
};

export const getQuoteExcessLabel = (excess, schemeTableId) => {
  if (schemeTableId === 1770) {
    if (excess.excessShortName === "Approved repair") return "Windscreen Repair";
    if (excess.excessShortName === "Approved replacement") {
      return "Windscreen Replace";
    }
  } else if (excess.excessShortName === "Windscreen Replacement") {
    return "Windscreen";
  }

  return excess.excessShortName;
};

export const getSortedQuoteExcesses = (excesses) =>
  [...excesses].sort((a, b) => {
    const aIsCompulsory =
      (a?.excessShortName ?? "").toLowerCase() === "compulsory";
    const bIsCompulsory =
      (b?.excessShortName ?? "").toLowerCase() === "compulsory";
    return Number(aIsCompulsory) - Number(bIsCompulsory);
  });

export const getQuotePanelItem = (quote, code) =>
  quote?.panelMemberPanelItems?.find(
    (item) => item.panelMemberPanelItemCode === code,
  );

export const getQuotePanelItemLines = (quote, code) =>
  getQuotePanelItem(quote, code)?.panelMemberPanelItemLines ?? [];

export const buildTqPanelQuoteRows = (panelQuoteData) => {
  const configs = (panelQuoteData?.panelMemberPanelConfigs ?? [])
    .slice()
    .sort((a, b) => a.order - b.order)
    .filter((config) => !["ER", "LRE"].includes(config.code));
  const quotes = panelQuoteData?.insurerResults?.quoted ?? [];

  return {
    configs,
    rows: quotes.map((quote, index) => {
      const excesses = getQuoteExcesses(quote);
      const paymentInFullOption = getQuotePaymentOption(
        quote,
        "Payment in Full",
      );
      const financeOption = getQuotePaymentOption(
        quote,
        "Finance - Deposit and 9 Installments",
      );

      return {
        quote,
        index,
        companyName: quote.companyName,
        schemeTableId: quote.schemeTableId,
        excessReduction: getQuoteExcessReduction(quote),
        paymentInFullOption,
        financeOption,
        financeCharge: getQuoteFinanceCharge(quote),
        annualPrice:
          paymentInFullOption?.totalAmountToPayByCustomer ?? quote.pricePerYear,
        cardExcesses: getSortedQuoteExcesses(
          quote.schemeTableId === 1770 ? excesses : excesses.slice(1),
        ),
        tableExcesses: getSortedQuoteExcesses(excesses),
        configRows: configs.map((config) => ({
          config,
          lines: getQuotePanelItemLines(quote, config.code),
          hasValue: Boolean(getQuotePanelItem(quote, config.code)),
        })),
        uninsuredDriver: quote.panelMemberQuoteFeatures?.find(
          (feature) => feature.featureName === "Uninsured Driver",
        ),
      };
    }),
  };
};
